import { useEffect, useRef, useState } from 'react'
import type { PolicyArtefactType, PolicyItem } from '@/lib/apiTypes'
import { useOrgPolicies, useOrgPolicySettings } from '@/lib/hooks'
import { SkeletonCard } from '@/shared/components/Skeleton'
import { TemplateGallery } from '@/features/policies/components/TemplateGallery'
import type { Template } from '@/features/policies/lib/regoTemplates'
import { BaselinePolicyModal, baselineToast } from '@/features/policies/components/BaselinePolicyModal'
import { useToast } from '@/context/ToastContext'
import { PolicyEnablementPanel } from '@/features/policies/components/PolicyEnablementPanel'
import { PolicyModal } from '@/features/policies/components/PolicyModal'
import { rescanNote } from '@/features/policies/lib/rescanNote'
import { PolicyDecisionsSummary } from '@/features/policies/components/PolicyDecisionsPanel'
import s from '@/shared/styles/policyTabs.module.css'
import { baselineDismissed, rememberBaselineDismissed } from '@/features/policies/lib/baselineDismissed'
import { ARTEFACT_NOUN, POLICY_SCOPES } from '@/features/policies/tabs/policyScopes'

// ── Overview: compliance KPIs, the 30-day decision summaries, enablement toggles ──
export function OverviewPanel({ orgId, canAdmin, canOfferBaseline, onJump, onPickPolicy }: {
  orgId: string
  canAdmin: boolean
  canOfferBaseline: boolean
  onJump: (k: PolicyArtefactType) => void
  onPickPolicy: (policyId: string) => void
}) {
  const policies = useOrgPolicies(orgId)
  const settings = useOrgPolicySettings(orgId)
  const { showToast } = useToast()
  const [gallery, setGallery] = useState(false)
  // The template a card in the gallery handed over, and the artefact set it came from: together
  // they are everything the create form needs, so the pick opens it already filled in.
  const [picked, setPicked] = useState<{ template: Template; artefactType: PolicyArtefactType } | null>(null)
  const [baseline, setBaseline] = useState(false)
  // Auto-open is a one-shot per mount: without this, dismissing the dialog and letting any
  // refetch land would reopen it on top of the person who just closed it.
  const offered = useRef(false)

  // An org with NO policies at all is the case the removed org-creation seed used to cover, so the
  // page offers the curated set unprompted rather than leaving an empty table and four switches.
  //
  // Declared above the loading early-return below (Rules of Hooks), and deliberately narrow:
  // an errored fetch leaves `data` undefined, which reads as "no policies" and would pop a
  // dialog over a page that failed to load.
  const noPolicies = !policies.loading && !policies.error && (policies.data?.policies.length ?? 0) === 0
  useEffect(() => {
    if (offered.current || !canOfferBaseline || !noPolicies) return
    offered.current = true
    if (baselineDismissed(orgId)) return
    setBaseline(true)
  }, [orgId, canOfferBaseline, noPolicies])

  if (policies.loading || settings.loading) return <SkeletonCard rows={4} />
  const list: PolicyItem[] = policies.data?.policies ?? []
  const data = settings.data
  // Read off the policy list rather than GET /policies/consumption-block, which is admin-only: a
  // reader whose init is being refused is exactly who most needs to see this.
  const armed = list.filter((p) => p.is_block_all && p.is_enabled)

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {armed.length > 0 && (
        <div
          role="status"
          style={{
            padding: '12px 14px', borderRadius: 8,
            border: '1px solid var(--red)', background: 'var(--bg2)',
          }}
        >
          <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--red)', marginBottom: 4 }}>
            All consumption is blocked for {armed.map((p) => ARTEFACT_NOUN[p.artefact_type]).join(', ')}
          </div>
          <div style={{ fontSize: '0.8rem', color: 'var(--text2)', lineHeight: 1.5 }}>
            An org owner armed the Danger-zone kill switch, so every one of these downloads is being
            refused regardless of the policies below. An owner can lift it in the organisation
            settings Danger zone.
          </div>
        </div>
      )}
      {gallery && (
        // No policy exists yet here, so the artefact is undecided: every set is insertable and the
        // card that is picked is what scopes the policy. `module` only sets which set opens first.
        <TemplateGallery
          artefactType="module"
          anyArtefact
          onPick={(template, artefactType) => setPicked({ template, artefactType })}
          onClose={() => setGallery(false)}
        />
      )}

      {picked && (
        <PolicyModal
          orgId={orgId}
          artefactType={picked.artefactType}
          template={picked.template}
          onClose={() => setPicked(null)}
          onSaved={() => { setPicked(null); policies.refetch(); showToast('Policy saved.', 'success') }}
        />
      )}

      {baseline && (
        // Unscoped: all three gating artefacts side by side, because from here no artefact has
        // been chosen and the recommendation spans them.
        <BaselinePolicyModal
          orgId={orgId}
          existing={list}
          onClose={() => { setBaseline(false); rememberBaselineDismissed(orgId) }}
          onApplied={(result) => {
            setBaseline(false)
            policies.refetch()
            settings.refetch()
            showToast(baselineToast(result, rescanNote), 'success')
          }}
        />
      )}

      <div className={s.topStrip}>
        <span className={s.monoLabel}>
          // {list.length} polic{list.length === 1 ? 'y' : 'ies'} across {POLICY_SCOPES.length} artefacts
        </span>
        {/* Same entry point as the one inside the editor, one step earlier: from here a template
            is a way IN to a policy, not a way to fill one that is already open. Admin-only,
            because that is who can create the policy it leads to. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {canOfferBaseline && (
            <button className="btn btn-ghost btn-sm" onClick={() => setBaseline(true)}>
              Add recommended
            </button>
          )}
          {canAdmin && (
            <button className="btn btn-ghost btn-sm" onClick={() => setGallery(true)}>
              Browse templates
            </button>
          )}
        </div>
      </div>

      <div className={s.kpiGrid}>
        {POLICY_SCOPES.map((a) => {
          // Authored rules only: the kill switch is not a rule anyone wrote, it has its own
          // banner above, and counting it would inflate "enforcing" by one for no insight.
          const scoped   = list.filter((p) => p.artefact_type === a.key && !p.is_block_all)
          const enforcing = scoped.filter((p) => p.is_enforcing && p.is_enabled).length
          const blocked  = data ? a.blocked(data) : 0
          return (
            <button key={a.key} className={s.kpiTile} onClick={() => onJump(a.key)} aria-label={`Open ${a.label} policies`}>
              <div className={s.kpiHead}>
                <span className={s.kpiLabel}>{a.label}</span>
                {a.live
                  ? <span className="badge badge-green" style={{ fontSize: '0.6rem' }}>live</span>
                  : <span className="badge badge-neutral" style={{ fontSize: '0.6rem' }}>pending scanner</span>}
              </div>
              <div className={s.kpiRow}>
                <div className={s.kpiStat}><span className={s.kpiNum}>{scoped.length}</span><span className={s.kpiUnit}>policies</span></div>
                <div className={s.kpiStat}><span className={s.kpiNum}>{enforcing}</span><span className={s.kpiUnit}>enforcing</span></div>
                <div className={s.kpiStat}>
                  <span className={s.kpiNum} style={{ color: blocked > 0 ? 'var(--red)' : 'var(--text)' }}>{blocked}</span>
                  <span className={s.kpiUnit}>blocked</span>
                </div>
              </div>
              <span className={s.kpiCta}>View {a.label.toLowerCase()} →</span>
            </button>
          )
        })}
      </div>

      {/* What the tiles' `blocked` counts cannot say: how much was refused, by which rule, and
          whether it is getting worse. These are 30-day WINDOW summaries, which is why they sit
          with the other summaries rather than above the ledger they used to push off-screen.
          Reading before configuring, so they come before the toggles. */}
      <PolicyDecisionsSummary orgId={orgId} onPickPolicy={onPickPolicy} />

      {/* The master + per-artefact enablement toggles live here on Overview. */}
      <PolicyEnablementPanel orgId={orgId} canAdmin={canAdmin} />
    </div>
  )
}
